import axios from 'axios';
import autoComplete from '@tarekraafat/autocomplete.js';
import { htmlEscape } from 'escape-goat';
import Fuse from 'fuse.js';

const SERVICES_URL = '/wp-json/ppshp/v1/services';
const CATEGORIES_URL = '/wp-json/ppshp/v1/service_categories';

class ServiceSearch {
  constructor({
    // Default options.
    rootEl = null,
  } = {}) {
    this.rootEl = rootEl;
    this.textForm = rootEl.querySelector('form[data-action="local-service-search"]');
    this.filterForms = rootEl.querySelectorAll('form[data-action="local-service-search-filter"]');
    this.serviceItemsContainer = rootEl.querySelector('.local-service-search__list-items');
    this.textInput = rootEl.querySelector('form[data-action="local-service-search"] input');
    this.itemTemplate = rootEl.querySelector('.local-service-search-list-item-template');

    this.rootUrl = rootEl.dataset.rootUrl;

    this.allServices = []; // Saves the initial set of services.
    this.currentServices = []; // This is reflected to DOM.

    this.filters = {
      searchTerm: '',
      categories: [],
      sites: [],
    };

    this.autocomplete = null;
    this.fuse = null;
  }

  setLoading(state) {
    if (state === true) {
      this.rootEl.classList.add('is--loading');
    }

    if (state === false) {
      this.rootEl.classList.remove('is--loading');
    }
  }

  // Data fetching functions.

  async fetchData() {
    const servicesResult = await this.fetchServices();

    if (servicesResult.status === 200) {
      this.allServices = servicesResult.data.services;
      this.currentServices = servicesResult.data.services;
    }
  }

  fetchServices() {
    return axios.get(`${this.rootUrl}${SERVICES_URL}`);
  }

  fetchCategories() {
    return axios.get(`${this.rootUrl}${CATEGORIES_URL}`);
  }

  fetchServicesByCategory(slug = '') {
    return axios.get(`${this.rootUrl}${SERVICES_URL}?cat=${htmlEscape(slug)}`);
  }

  // Event handlers.

  onInitialDataFetched() {
    const that = this;

    that.textForm.addEventListener('submit', (e) => { that.onTextFormSubmit(e); });

    [...that.filterForms].forEach((form) => {
      form.addEventListener('change', (e) => { that.onFilterFormChange(e); });
      form.addEventListener('submit', (e) => { e.preventDefault(); });
    });

    that.runFilters();

    that.fuse = new Fuse(that.allServices, { keys: ['title'] });

    // eslint-disable-next-line new-cap
    that.autocomplete = new autoComplete({
      selector: () => that.textInput,
      data: {
        src: that.allServices,
        keys: ['title'],
      },
      submit: true,
      threshold: 2,
      debounce: 150,
      resultsList: {
        noResults: true,
        maxResults: 8,
        tabSelect: true,
      },
      resultItem: {
        element: (item, data) => {
          // eslint-disable-next-line no-param-reassign
          item.innerHTML = `
          <div class="local-service-search-dropdown__result">
            <span class="title">${data.match}</span>
            <span class="site">${data.value.site.name}</span>
          </div>
          `;
        },
        highlight: true,
      },
      events: {
        input: {
          focus() {
            if (that.autocomplete.input.value.length) {
              that.autocomplete.start();
            }
          },
          selection(event) {
            const { detail } = event;
            that.autocomplete.input.blur();
            that.autocomplete.input.value = detail.selection.value.title;
            that.textForm.requestSubmit();
          },
        },
      },
    });
  }

  onTextFormSubmit(e) {
    e.preventDefault();

    const formData = new FormData(this.textForm);
    this.filters.searchTerm = formData.get('search_term');
    this.runFilters();
  }

  onFilterFormChange(e) {
    // Note that there may be multiple filter forms.
    // Filters will always be selected from the form they are changed from.
    const formData = new FormData(e.target.closest('form'));
    this.filters.categories = formData.getAll('category[]').filter((i) => i !== '');
    this.filters.sites = formData.getAll('multisite[]').filter((i) => i !== '');
    this.runFilters();
  }

  // Actions.

  runFilters() {
    // Text search utilizing Fuse.js fuzzy search.
    this.currentServices = (this.filters.searchTerm === '')
      ? this.allServices
      : this.fuse.search(this.filters.searchTerm).map((r) => r.item);

    // Filter out items that do not belong to selected categories or multisite sites.
    this.currentServices = this.currentServices.filter((item) => {
      const matchesCategory = this.filters.categories.length > 0
        ? item.categories.some((cat) => this.filters.categories.includes(cat.name))
        : true;

      const matchesSite = this.filters.sites.length > 0
        ? this.filters.sites.includes(item.site.name)
        : true;

      return (matchesCategory && matchesSite);
    });

    this.populateItems(this.currentServices);
  }

  populateItems(services = []) {
    const that = this;
    const servicesIntent = document.createDocumentFragment();

    services.forEach((service) => {
      const node = that.itemTemplate.content.cloneNode(true);
      // const categoriesStr = service.categories.map((i) => i.name).join(', ');

      node.querySelector('a').href = service.link;
      node.querySelector('.title').innerHTML = service.title;
      node.querySelector('.categories').innerHTML = service.categories.map((i) => i.name).join(', ') || 'N/A';

      servicesIntent.appendChild(node);
    });

    that.serviceItemsContainer.innerHTML = '';
    that.serviceItemsContainer.appendChild(servicesIntent);
  }

  mount() {
    const that = this;

    if (that.rootEl) {
      that.setLoading(true);
      that.fetchData().then(() => {
        that.onInitialDataFetched();
        that.setLoading(false);
      });
    }
  }
}

export default function init() {
  const roots = document.querySelectorAll('.section--local-service-search');

  [...roots].forEach((root) => {
    new ServiceSearch({ rootEl: root }).mount();
  });
}
